// 使用說明視窗：快速上手（快捷鍵）、用 Claude 產生（prompt）、讓 Claude 直接畫（三種方式比較）。
import { t, lang, type I18nKey } from './i18n.js';

interface Prompts {
  outline: Record<string, string>;
  connector: Record<string, { url: string; claudeAi: string; claudeCode: string; ask: string }>;
}

const mod = /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl';

const KEYS: [string, I18nKey][] = [
  ['Tab', 'key.child'],
  ['Enter / Shift+Enter', 'key.sibling'],
  [`${mod}+Enter`, 'key.parent'],
  ['Delete', 'key.delete'],
  ['F2 / Space', 'key.edit'],
  ['← ↑ → ↓', 'key.nav'],
  ['Alt+↑ / Alt+↓', 'key.reorder'],
  [`${mod}+/`, 'key.collapse'],
  ['Alt+R', 'key.relation'],
  [`${mod}+C / X / V`, 'key.copy'],
  [`${mod}+Z / ${mod}+Shift+Z`, 'key.undo'],
  [`${mod}+0`, 'key.fit'],
  [`${mod}+= / ${mod}+-`, 'key.zoom'],
  [`${mod}+S`, 'key.save'],
  ['F5', 'view.play'],
];

const MOUSE: I18nKey[] = ['mouse.select', 'mouse.drag', 'mouse.pan', 'mouse.zoom', 'mouse.badge'];

function el<K extends keyof HTMLElementTagNameMap>(tag: K, props: Partial<HTMLElementTagNameMap[K]> & { className?: string } = {}, children: (Node | string)[] = []): HTMLElementTagNameMap[K] {
  const element = Object.assign(document.createElement(tag), props);
  element.append(...children);
  return element;
}

export class Help {
  private prompts: Prompts | null = null;
  private readonly panes = new Map<string, HTMLElement>();
  private readonly tabs = new Map<string, HTMLButtonElement>();

  constructor(private readonly dialog: HTMLDialogElement, private readonly actions: { copy(text: string): void; openPaste(): void }) {
    this.build();
  }

  private build(): void {
    const header = el('header', { className: 'dialog-head' }, [el('h2', { textContent: t('help.title') })]);
    const close = el('button', { type: 'button', className: 'icon-button', textContent: '✕', title: t('help.close') });
    close.addEventListener('click', () => this.dialog.close());
    header.append(close);
    const tabBar = el('nav', { className: 'tabs' });
    const body = el('div', { className: 'dialog-body' });
    for (const [key, label] of [['start', 'help.tabStart'], ['claude', 'help.tabClaude'], ['connect', 'help.tabConnect']] as const) {
      const tab = el('button', { type: 'button', className: 'tab', textContent: t(label) });
      tab.addEventListener('click', () => this.show(key));
      tabBar.append(tab);
      this.tabs.set(key, tab);
      const pane = el('div', { className: 'pane' });
      body.append(pane);
      this.panes.set(key, pane);
    }
    this.dialog.append(header, tabBar, body);
    this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); });
    this.renderStart();
  }

  async open(tab: 'start' | 'claude' | 'connect' = 'start'): Promise<void> {
    this.show(tab);
    if (!this.dialog.open) this.dialog.showModal();
    if (!this.prompts) {
      try {
        this.prompts = await (await fetch('/api/prompts')).json() as Prompts;
        this.renderClaude();
        this.renderConnect();
      } catch {
        this.panes.get('claude')!.textContent = t('error.network');
      }
    }
  }

  private show(key: string): void {
    for (const [name, pane] of this.panes) pane.hidden = name !== key;
    for (const [name, tab] of this.tabs) tab.classList.toggle('active', name === key);
  }

  private renderStart(): void {
    const table = el('table', { className: 'keys' });
    for (const [keys, label] of KEYS) table.append(el('tr', {}, [el('td', {}, [el('kbd', { textContent: keys })]), el('td', { textContent: t(label) })]));
    const mouse = el('ul', { className: 'plain' }, MOUSE.map(key => el('li', { textContent: t(key) })));
    this.panes.get('start')!.append(el('h3', { textContent: t('help.keys') }), table, el('h3', { textContent: t('help.mouse') }), mouse);
  }

  private renderClaude(): void {
    const prompt = this.prompts!.outline[lang] ?? this.prompts!.outline.en!;
    const steps = el('ol', { className: 'steps' }, (['1', '2', '3'] as const).map(step => el('li', {}, [
      el('strong', { textContent: t(`claude.s${step}` as I18nKey) }), el('span', { textContent: t(`claude.s${step}d` as I18nKey) }),
    ])));
    const copy = el('button', { type: 'button', className: 'button primary', textContent: t('claude.copy') });
    copy.addEventListener('click', () => this.actions.copy(prompt));
    const open = el('button', { type: 'button', className: 'button', textContent: t('claude.open') });
    open.addEventListener('click', () => { this.dialog.close(); this.actions.openPaste(); });
    this.panes.get('claude')!.append(steps, el('div', { className: 'row' }, [copy, open]), el('pre', { className: 'prompt', textContent: prompt }));
  }

  private renderConnect(): void {
    const connector = this.prompts!.connector[lang] ?? this.prompts!.connector.en!;
    const rows: [I18nKey, I18nKey, I18nKey, I18nKey, I18nKey][] = (['paste', 'mcp', 'skill'] as const).map(way => [
      `connect.${way}` as I18nKey, `connect.${way}.who` as I18nKey, `connect.${way}.setup` as I18nKey, `connect.${way}.edit` as I18nKey, `connect.${way}.output` as I18nKey,
    ]);
    const head = el('tr', {}, (['connect.col.way', 'connect.col.who', 'connect.col.setup', 'connect.col.edit', 'connect.col.output'] as I18nKey[]).map(key => el('th', { textContent: t(key) })));
    const table = el('table', { className: 'compare' }, [el('thead', {}, [head]), el('tbody', {}, rows.map(cells => el('tr', {}, cells.map((key, index) => el(index ? 'td' : 'th', { textContent: t(key) })))))]);

    const copyRow = (text: string) => {
      const button = el('button', { type: 'button', className: 'button', textContent: t('connect.copy') });
      button.addEventListener('click', () => this.actions.copy(text));
      return el('div', { className: 'copy-row' }, [el('code', { textContent: text }), button]);
    };
    const mcp = el('section', { className: 'card' }, [
      el('h3', { textContent: t('connect.mcpSteps') }), copyRow(connector.url), el('p', { textContent: connector.claudeAi }),
      el('p', { className: 'muted', textContent: 'Claude Code:' }), copyRow(connector.claudeCode), el('p', { className: 'muted', textContent: connector.ask }),
    ]);
    const skill = el('section', { className: 'card' }, [
      el('h3', { textContent: t('connect.skillSteps') }), el('p', { textContent: t('connect.skillHow') }),
      el('a', { className: 'button primary', href: '/download/sind-skill.zip', textContent: t('connect.download') }),
    ]);
    this.panes.get('connect')!.append(el('p', { textContent: t('connect.intro') }), table, el('div', { className: 'cards' }, [mcp, skill]));
  }
}
